import { useContext, useEffect, useState } from 'react';

import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import scssVariables from '@styles/variables.module.scss';
import { UppyContext, useDropzone, useUppyState } from '@uppy/react';
import sx from 'classnames';
import { useTranslation } from 'react-i18next';

import styles from './FileUploadDropzone.module.scss';
import { Icon } from '../Icon';
import { uiIcons } from '@/constants/uiIcons';

interface FileUploadDropzoneProps {
  onFilesChanged: (files: File[]) => void;
  hideDropzoneUntilDrag: boolean;
  disabled?: boolean;
  dropzoneHelperText?: string;
  disabledHelperText?: string;
}
export const FileUploadDropzone = ({
  onFilesChanged,
  hideDropzoneUntilDrag,
  disabled = false,
  dropzoneHelperText,
  disabledHelperText,
}: FileUploadDropzoneProps) => {
  const { uppy } = useContext(UppyContext);
  const { t } = useTranslation('ui');
  const [isFileDraggingWindow, setIsFileDraggingWindow] = useState(false);
  const [isFileDragOverDropzone, setIsFileDragOverDropzone] = useState(false);

  if (!uppy) {
    throw new Error(
      'FileUploadDropzone must be used within UppyContextProvider',
    );
  }

  const uppyState = useUppyState(uppy, (state) => state);

  const { getRootProps, getInputProps } = useDropzone({
    noClick: false,
    onDragOver: () => {
      setIsFileDragOverDropzone(true);
    },
    onDragEnter: () => {
      setIsFileDragOverDropzone(true);
    },
    onDragLeave: () => {
      setIsFileDragOverDropzone(false);
    },
    onDrop: (files) => {
      setIsFileDragOverDropzone(false);
      onFilesChanged(files || []);
    },
    onFileInputChange: (files) => {
      onFilesChanged(files || []);
    },
  });

  const displayDropZone =
    !hideDropzoneUntilDrag || isFileDraggingWindow || isFileDragOverDropzone;

  const currentUploadLength =
    Object.values(uppyState.currentUploads)[0]?.fileIDs.length ?? 0;

  useEffect(() => {
    let dragCounter = 0;

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      dragCounter++;
      if (e.dataTransfer?.types.includes('Files')) {
        setIsFileDraggingWindow(true);
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounter--;

      if (dragCounter === 0) {
        setIsFileDraggingWindow(false);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'none';
        e.dataTransfer.dropEffect = 'none';
      }
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounter = 0;
      setIsFileDraggingWindow(false);
    };

    window.addEventListener('dragenter', handleDragEnter, false);
    window.addEventListener('dragleave', handleDragLeave, false);
    window.addEventListener('dragover', handleDragOver, false);
    window.addEventListener('drop', handleDrop, false);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter, false);
      window.removeEventListener('dragleave', handleDragLeave, false);
      window.removeEventListener('dragover', handleDragOver, false);
      window.removeEventListener('drop', handleDrop, false);
    };
  }, []);

  return (
    <div
      className={sx(styles.FileUploadDropzoneWrapper, {
        [styles.FileUploadDropzoneWrapperHidden]: !displayDropZone,
      })}
    >
      {currentUploadLength > 0 ? (
        <Stack
          className={styles.UploadingOverlay}
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress color="inherit" thickness={4} />
          <Typography variant="bodySemiBold" color="textSecondary">
            {t('fileUploadDropzone.UploadingFilesText', {
              count: currentUploadLength,
            })}
          </Typography>
        </Stack>
      ) : (
        displayDropZone &&
        (disabled ? (
          <Stack
            className={sx(styles.FileUploadDropzone, {
              [styles.FileUploadDropzoneDisabled]: disabled,
            })}
            alignItems="center"
            justifyContent="center"
          >
            <Icon
              name={uiIcons.file.unavailable}
              size="xxLarge"
              color="textSecondary"
            />
            <Typography variant="bodySemiBold" color="textSecondary">
              {disabledHelperText || t('fileUploadDropzone.DisabledHelperText')}
            </Typography>
          </Stack>
        ) : (
          <>
            <input className={styles.HiddenInput} {...getInputProps()} />
            <div
              {...getRootProps()}
              className={sx(styles.FileUploadDropzone, {
                [styles.FileUploadDropzoneGlobalDragActive]:
                  isFileDraggingWindow && !isFileDragOverDropzone,
                [styles.FileUploadDropzoneDragOver]: isFileDragOverDropzone,
              })}
            >
              {!isFileDraggingWindow && !isFileDragOverDropzone && (
                <Stack alignItems="center">
                  <Button
                    variant="contained"
                    startIcon={<Icon name={uiIcons.actions.upload} />}
                    onClick={(e) => {
                      /*
                       * explicitly stop propagation to prevent parent div onClick
                       * and call uppy on click handler directly
                       */
                      e.stopPropagation();
                      getRootProps().onClick?.();
                    }}
                    onKeyDown={(e) => {
                      /*
                       * Prevent Enter and Space from bubbling up to the parent div
                       * and prevent default synthetic button behavior
                       * then call uppy on click handler directly
                       */
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        e.preventDefault();
                        getRootProps().onClick?.();
                      }
                    }}
                    data-automation-id="file-upload-dropzone-file-input-button"
                  >
                    {t('fileUploadDropzone.FileInputButton')}
                  </Button>
                  <Typography variant="label" color="textSecondary">
                    {t('fileUploadDropzone.FileInputDropHelperText')}
                  </Typography>
                </Stack>
              )}

              {isFileDraggingWindow && !isFileDragOverDropzone && (
                <Stack alignItems="center" gap={4}>
                  <Stack alignItems="center">
                    <Icon
                      name={uiIcons.actions.upload}
                      size="large"
                      color={scssVariables.ColorBlue80}
                    />
                    <Typography variant="bodySemiBold" color="textSecondary">
                      {dropzoneHelperText ||
                        t('fileUploadDropzone.DropFilesHereToStartHelperText')}
                    </Typography>
                  </Stack>
                  <SupportedFileTypesHelper />
                </Stack>
              )}
              {isFileDragOverDropzone && (
                <Stack alignItems="center" gap={4}>
                  <Stack alignItems="center">
                    <Icon
                      name={uiIcons.actions.upload}
                      size="large"
                      color="success"
                    />
                    <Typography variant="bodySemiBold" color="textSecondary">
                      {t('fileUploadDropzone.DropFilesToStartHelperText')}
                    </Typography>
                  </Stack>
                  <SupportedFileTypesHelper />
                </Stack>
              )}
            </div>
          </>
        ))
      )}
    </div>
  );
};

const SupportedFileTypesHelper = () => {
  const { uppy } = useContext(UppyContext);
  const { t } = useTranslation('ui');

  // Get file restrictions from Uppy configuration
  const fileRestrictions =
    uppy?.getPlugin('FileInput')?.opts?.restrictions ||
    uppy?.opts?.restrictions;
  const allowedFileTypes = fileRestrictions?.allowedFileTypes;

  const supportedFileTypesList = allowedFileTypes
    ?.map((type) => type.split('/')[1])
    .join(', ');

  return (
    <Typography variant="label" color="textSecondary">
      {t('fileUploadDropzone.SupportedFileTypeLabel')}:{' '}
      <strong>{supportedFileTypesList}</strong>
    </Typography>
  );
};
